// CB:BEGIN pm-properties-page
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';
import SharedHeaderPropertyManager from '../components/SharedHeaderPropertyManager';
import ContentLimiter from '../components/layout/ContentLimiter';
import { Building2, Plus, Search } from 'lucide-react';

const BRAND_GREEN = '#123c3b';
const PROPERTY_TYPES = ['RESIDENTIAL', 'COMMERCIAL', 'MIXED_USE'];

interface Property {
  id: string;
  label?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  propertyType: string;
  notes?: string;
  _count?: { projects: number };
}

interface Props {
  onNavigate: (screen: string) => void;
}

// ---------------------------------------------------------------------------
// Add Property Modal (self-contained copy)
// ---------------------------------------------------------------------------
function AddPropertyModal({ onClose, onSaved, token }: { onClose: () => void; onSaved: () => void; token: string | null }) {
  const [form, setForm] = useState({ label: '', address1: '', address2: '', city: '', state: '', zip: '', propertyType: 'RESIDENTIAL', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSugg, setShowSugg] = useState(false);
  const [suggIdx, setSuggIdx] = useState(-1);
  const ignoreNext = useRef(false);
  const addrRef = useRef<HTMLInputElement>(null);

  const fetchSugg = async (input: string) => {
    if (input.length < 3) { setSuggestions([]); setShowSugg(false); return; }
    try {
      const r = await fetch(getApiUrl(`/api/autocomplete?input=${encodeURIComponent(input)}`));
      if (r.ok) { const d = await r.json(); if (d.predictions?.length) { setSuggestions(d.predictions); setShowSugg(true); setSuggIdx(-1); } else { setSuggestions([]); setShowSugg(false); } }
    } catch { /**/ }
  };

  const selectSugg = async (s: any) => {
    const street = (s.description || '').replace(/, USA$/, '').split(',')[0].trim();
    ignoreNext.current = true;
    setForm(f => ({ ...f, address1: street }));
    setSuggestions([]); setShowSugg(false);
    if (s.place_id) {
      try {
        const r = await fetch(getApiUrl(`/api/details?place_id=${encodeURIComponent(s.place_id)}`));
        if (r.ok) {
          const d = await r.json();
          const comps: any[] = d.result?.address_components || [];
          const get = (t: string) => comps.find((c: any) => c.types.includes(t));
          const addr1 = [[get('street_number')?.long_name, get('route')?.short_name].filter(Boolean).join(' ') || street][0];
          setForm(f => ({ ...f, address1: addr1, city: get('locality')?.long_name || get('sublocality')?.long_name || '', state: get('administrative_area_level_1')?.short_name || '', zip: get('postal_code')?.long_name || '' }));
        }
      } catch { /**/ }
    }
    setTimeout(() => addrRef.current?.focus(), 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const r = await fetch(getApiUrl('/api/property-manager/properties'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, credentials: 'include', body: JSON.stringify(form) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.error || 'Failed to save'); return; }
      onSaved(); onClose();
    } catch { setError('Network error'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 540, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Add Property</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Property Nickname (optional)</label>
            <input type="text" placeholder="e.g. Downtown Office, Unit 4B" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 16, position: 'relative' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Street Address</label>
            <input ref={addrRef} type="text" required placeholder="123 Main St" value={form.address1} autoComplete="off" onChange={e => { setForm(f => ({ ...f, address1: e.target.value })); if (ignoreNext.current) { ignoreNext.current = false; return; } fetchSugg(e.target.value); }} onKeyDown={e => { if (!showSugg) return; if (e.key === 'ArrowDown') { e.preventDefault(); setSuggIdx(i => Math.min(i + 1, suggestions.length - 1)); } else if (e.key === 'ArrowUp') { e.preventDefault(); setSuggIdx(i => Math.max(i - 1, -1)); } else if (e.key === 'Enter' && suggIdx >= 0) { e.preventDefault(); selectSugg(suggestions[suggIdx]); } else if (e.key === 'Escape') setShowSugg(false); }} onBlur={() => setTimeout(() => setShowSugg(false), 150)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
            {showSugg && suggestions.length > 0 && (
              <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, marginTop: 2, padding: 0, listStyle: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                {suggestions.map((s, i) => <li key={s.place_id || i} onMouseDown={() => selectSugg(s)} style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', background: i === suggIdx ? '#f0fdf4' : '#fff', color: '#111827', borderBottom: i < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none' }}>{(s.description || '').replace(/, USA$/, '')}</li>)}
              </ul>
            )}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Suite / Unit (optional)</label>
            <input type="text" placeholder="Apt 2B" value={form.address2} onChange={e => setForm(f => ({ ...f, address2: e.target.value }))} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[{ label: 'City', key: 'city', ph: 'Nashville' }, { label: 'State', key: 'state', ph: 'TN' }, { label: 'ZIP', key: 'zip', ph: '37201' }].map(f => (
              <div key={f.key}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{f.label}</label>
                <input required placeholder={f.ph} value={(form as any)[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Property Type</label>
            <select value={form.propertyType} onChange={e => setForm(f => ({ ...f, propertyType: e.target.value }))} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' }}>
              {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Notes (optional)</label>
            <textarea placeholder="Access instructions, tenant contacts, HOA rules, insurance details, etc." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          <button type="submit" disabled={saving} style={{ width: '100%', padding: 12, background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving...' : 'Add Property'}</button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Import Modal (CSV/Excel)
// ---------------------------------------------------------------------------
function ImportPropertiesModal({ onClose, onImported, token }: { onClose: () => void; onImported: () => void; token: string | null }) {
  type PreviewRow = { rowNum: number; label: string | null; address1: string; city: string; state: string; zip: string; propertyType: string; isDuplicate?: boolean; errors?: string[] };
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<{ validRows: PreviewRow[]; errorRows: any[]; total: number } | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; failed: number } | null>(null);
  const [error, setError] = useState('');

  const parseFile = async (file: File) => {
    setFileName(file.name);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(getApiUrl('/api/pm/v3/import/parse-file'), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to parse file'); return; }
      try {
        const validateRes = await fetch(getApiUrl('/api/pm/v3/import/validate'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, credentials: 'include', body: JSON.stringify({ rows: data.rows }) });
        const validateData = await validateRes.json();
        if (!validateRes.ok) { setError(validateData.error || 'Validation failed'); return; }
        setPreview(validateData); setStep('preview');
      } catch { setError('Network error during validation'); }
    } catch { setError('Network error during file parse'); }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setImporting(true); setError('');
    try {
      const res = await fetch(getApiUrl('/api/pm/v3/import/confirm'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, credentials: 'include', body: JSON.stringify({ rows: preview.validRows, fileName }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Import failed'); return; }
      setResult({ imported: data.imported, failed: data.failed || 0 }); setStep('done'); onImported();
    } catch { setError('Network error during import'); } finally { setImporting(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 580, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>Import Properties</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}
        {step === 'upload' && (
          <div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Upload a CSV or Excel file (.csv, .xlsx). Required columns: <strong>address, city, state, zip</strong>. Optional: name, propertyType, notes.</p>
            <div style={{ marginBottom: 12 }}><a href={getApiUrl('/api/pm/v3/import/template')} style={{ fontSize: 13, color: '#2563eb', textDecoration: 'underline' }}>Download template CSV</a></div>
            <label style={{ display: 'block', border: '2px dashed #d1d5db', borderRadius: 10, padding: '32px 16px', textAlign: 'center', cursor: 'pointer', background: '#f9fafb' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Click to choose file</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>CSV or Excel (.csv, .xlsx)</div>
              <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) parseFile(f); }} />
            </label>
          </div>
        )}
        {step === 'preview' && preview && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {[{ label: 'Total', value: preview.total, color: '#374151' }, { label: 'Valid', value: preview.validRows.length, color: '#059669' }, { label: 'Errors', value: preview.errorRows.length, color: preview.errorRows.length > 0 ? '#dc2626' : '#94a3b8' }].map(s => (
                <div key={s.label} style={{ flex: 1, background: '#f9fafb', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{s.label}</div>
                </div>
              ))}
            </div>
            {preview.validRows.slice(0, 5).map(r => (
              <div key={r.rowNum} style={{ fontSize: 12, padding: '6px 10px', background: r.isDuplicate ? '#fffbeb' : '#f0fdf4', borderRadius: 6, marginBottom: 4 }}>
                <strong>{r.address1}</strong>, {r.city}, {r.state} {r.zip}{r.label && <span style={{ color: '#64748b' }}> — {r.label}</span>}{r.isDuplicate && <span style={{ color: '#d97706', marginLeft: 6 }}>⚠ possible duplicate</span>}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => setStep('upload')} style={{ flex: 1, padding: 10, border: '1px solid #d1d5db', borderRadius: 8, background: '#f9fafb', fontSize: 14, cursor: 'pointer' }}>Back</button>
              <button onClick={handleConfirm} disabled={importing || preview.validRows.length === 0} style={{ flex: 2, padding: 10, background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: importing || preview.validRows.length === 0 ? 'not-allowed' : 'pointer', opacity: importing || preview.validRows.length === 0 ? 0.6 : 1 }}>{importing ? 'Importing…' : `Import ${preview.validRows.length} Propert${preview.validRows.length === 1 ? 'y' : 'ies'}`}</button>
            </div>
          </div>
        )}
        {step === 'done' && result && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Import complete</div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>{result.imported} propert{result.imported === 1 ? 'y' : 'ies'} added{result.failed > 0 ? `, ${result.failed} skipped` : ''}.</div>
            <button onClick={onClose} style={{ padding: '10px 28px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function PropertyManagerPropertiesPage({ onNavigate }: Props) {
  const { getToken } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const freshToken = await getToken().catch(() => null);
      if (!freshToken) { setLoading(false); return; }
      setToken(freshToken);
      const r = await fetch(getApiUrl('/api/property-manager/properties'), { headers: { Authorization: `Bearer ${freshToken}` }, credentials: 'include' });
      if (r.ok) { const d = await r.json(); setProperties(d.properties || []); }
    } finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const filtered = properties.filter(p => {
    if (filterType && p.propertyType !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      return (p.label || '').toLowerCase().includes(q) || p.address1.toLowerCase().includes(q) || p.city.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-properties" />

      <ContentLimiter>
        <div style={{ padding: '32px 0' }}>
          {/* Page header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 }}>Properties</h1>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{properties.length} total in your portfolio</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowImport(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>↑ Import</button>
              <button onClick={() => setShowAdd(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Plus size={14} /> Add Property</button>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 220px' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input placeholder="Search by name or address…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', padding: '9px 12px 9px 32px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', boxSizing: 'border-box' }} />
            </div>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#374151' }}>
              <option value="">All Types</option>
              {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </div>

          {/* Properties grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Building2 size={40} style={{ color: '#cbd5e1', marginBottom: 12 }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{search || filterType ? 'No matching properties' : 'No properties yet'}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>{search || filterType ? 'Try adjusting your filters.' : 'Add your first property to get started.'}</div>
              {!search && !filterType && <button onClick={() => setShowAdd(true)} style={{ padding: '10px 24px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Add First Property</button>}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {filtered.map(prop => (
                <button
                  key={prop.id}
                  onClick={() => onNavigate(`/pm/properties/${prop.id}`)}
                  style={{ width: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, cursor: 'pointer', textAlign: 'left', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'box-shadow 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: prop.propertyType === 'COMMERCIAL' ? '#eff6ff' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Building2 size={18} color={prop.propertyType === 'COMMERCIAL' ? '#3b82f6' : '#16a34a'} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      {prop.label && <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prop.label}</div>}
                      <div style={{ fontSize: 13, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prop.address1}, {prop.city}, {prop.state} {prop.zip}</div>
                      {prop.notes && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prop.notes}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{prop.propertyType.replace('_', ' ')}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{prop._count?.projects ?? 0} project{(prop._count?.projects ?? 0) !== 1 ? 's' : ''}</div>
                    </div>
                    <span style={{ fontSize: 18, color: '#cbd5e1' }}>›</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </ContentLimiter>

      {showAdd && <AddPropertyModal onClose={() => setShowAdd(false)} onSaved={fetchProperties} token={token} />}
      {showImport && <ImportPropertiesModal onClose={() => setShowImport(false)} onImported={fetchProperties} token={token} />}
    </div>
  );
}
// CB:END pm-properties-page
